import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { getSearchHistory, getSearch } from '../services/searchHistoryService';
import { personPath } from '../components/statistics/StatisticsPage';
import { formatDateTime, formatDecimal, WINDOWS } from '../utils/period';
import './Users.css';
import '../pages/Invitations.css';
import './SearchHistory.css';

interface SearchRow {
  id: number;
  createdAt: string;
  user: { id: number; fullName: string | null; email: string | null };
  origin: string | null;
  destination: string | null;
  departureDate: string | null;
  startDate: string | null;
  endDate: string | null;
  timeSlots: string[];
  resultCount: number;
  topScore: number | null;
}

interface ShownRide {
  rank: number;
  score: number | null;
  pickupKm: number | null;
  dropoffKm: number | null;
  rideId: number;
  link: string;
  ride: {
    departureDate: string | null;
    departureTime: string | null;
    status: string;
    price: number | null;
    seatsOffered: number;
    seatsBooked: number;
    driver: { id: number; fullName: string | null } | null;
    origin: string;
    destination: string;
  } | null;
}

interface Expanded {
  loading: boolean;
  error: string | null;
  rides: ShownRide[];
}

const errorText = (err: any, fallback: string) => err?.response?.data?.message || fallback;

const SLOT_LABELS: Record<string, string> = Object.fromEntries(WINDOWS.map((w) => [w.key, w.label]));

/** "2026-10-08" → "8 Oct 2026" (calendar date, no zone shift). */
function dayText(ymd: string | null): string {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const searchedDates = (s: SearchRow) =>
  s.startDate && s.endDate ? `${dayText(s.startDate)} – ${dayText(s.endDate)}` : dayText(s.departureDate);

const slotsText = (slots: string[]) =>
  slots.length ? slots.map((s) => SLOT_LABELS[s]?.split(' ')[0] || s).join(', ') : 'Any time';

const scoreText = (score: number | null) => (score === null ? '—' : `${Math.round(score * 100)}%`);

const kmText = (km: number | null) => (km === null ? '—' : `${formatDecimal(km)} km`);

const priceText = (price: number | null) => (price === null ? '—' : price === 0 ? 'Free' : `₪${formatDecimal(price)}`);

function departureText(ride: ShownRide['ride']): string {
  if (!ride?.departureDate) return '—';
  const day = dayText(ride.departureDate);
  return ride.departureTime ? `${day}, ${String(ride.departureTime).slice(0, 5)}` : day;
}

/**
 * Search history: every ride search a student ran in the app over the last 10
 * days — what they asked for, how many rides matched, and (expanded) the rides
 * they were shown in rank order, with links to the real rides.
 */
const SearchHistory = () => {
  const [searches, setSearches] = useState<SearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const limit = 20;
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  // Text filters are typed into *Input and applied debounced.
  const [userInput, setUserInput] = useState('');
  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [text, setText] = useState({ user: '', origin: '', destination: '' });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [timeSlot, setTimeSlot] = useState('');

  const [expanded, setExpanded] = useState<Record<number, Expanded>>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = { user: userInput.trim(), origin: originInput.trim(), destination: destinationInput.trim() };
      if (next.user === text.user && next.origin === text.origin && next.destination === text.destination) return;
      setText(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInput, originInput, destinationInput]);

  useEffect(() => {
    loadSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, text, from, to, timeSlot]);

  const loadSearches = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string | number> = { page, limit };
      if (text.user) params.user = text.user;
      if (text.origin) params.origin = text.origin;
      if (text.destination) params.destination = text.destination;
      if (from) params.from = from;
      if (to) params.to = to;
      if (timeSlot) params.timeSlot = timeSlot;

      const response = await getSearchHistory(params);
      setSearches(response.data.searches || []);
      setPagination(response.data.pagination || { total: 0, pages: 1 });
      setExpanded({});
    } catch (err) {
      setError(errorText(err, 'Failed to load search history'));
    } finally {
      setLoading(false);
    }
  };

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const toggle = async (search: SearchRow) => {
    if (expanded[search.id]) {
      setExpanded(({ [search.id]: _closed, ...rest }) => rest);
      return;
    }
    setExpanded((prev) => ({ ...prev, [search.id]: { loading: true, error: null, rides: [] } }));
    try {
      const response = await getSearch(search.id);
      setExpanded((prev) =>
        prev[search.id] ? { ...prev, [search.id]: { loading: false, error: null, rides: response.data.rides || [] } } : prev
      );
    } catch (err) {
      setExpanded((prev) =>
        prev[search.id]
          ? { ...prev, [search.id]: { loading: false, error: errorText(err, 'Failed to load rides'), rides: [] } }
          : prev
      );
    }
  };

  const clearFilters = () => {
    setUserInput('');
    setOriginInput('');
    setDestinationInput('');
    setFrom('');
    setTo('');
    setTimeSlot('');
    setPage(1);
  };

  const hasFilters = userInput || originInput || destinationInput || from || to || timeSlot;

  return (
    <div className="invitations-page">
      <div className="invitations-header">
        <div>
          <h1>Search history</h1>
          <p className="subtitle">
            Every ride search students ran in the last 10 days, how many rides matched, and the rides they were shown
          </p>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '20px' }}>
          <div className="alert alert-error">{error}</div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="filters">
            <input
              type="text"
              placeholder="User: name, email or #id"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="Origin"
              value={originInput}
              onChange={(e) => setOriginInput(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="Destination"
              value={destinationInput}
              onChange={(e) => setDestinationInput(e.target.value)}
              className="search-input"
            />

            <label className="filter-date">
              <span>From</span>
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => resetPage(setFrom)(e.target.value)}
                className="filter-select"
              />
            </label>
            <label className="filter-date">
              <span>To</span>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => resetPage(setTo)(e.target.value)}
                className="filter-select"
              />
            </label>

            <select value={timeSlot} onChange={(e) => resetPage(setTimeSlot)(e.target.value)} className="filter-select">
              <option value="">All time slots</option>
              {WINDOWS.map((w) => (
                <option key={w.key} value={w.key}>
                  {w.label}
                </option>
              ))}
              <option value="any">Any time (no slot chosen)</option>
            </select>

            {hasFilters && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>

        {loading && <div className="loading">Loading searches...</div>}

        {!loading && !error && searches.length === 0 && (
          <div className="empty-state">
            <p>No searches found.</p>
          </div>
        )}

        {!loading && !error && searches.length > 0 && (
          <>
            <div className="table-container">
              <table className="invitations-table search-history-table">
                <thead>
                  <tr>
                    <th aria-label="Expand" />
                    <th>Searched at</th>
                    <th>User</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Ride date</th>
                    <th>Time slots</th>
                    <th>Rides found</th>
                    <th title="Matching score of the top-ranked ride">Top score</th>
                  </tr>
                </thead>
                <tbody>
                  {searches.map((s) => {
                    const open = expanded[s.id];
                    return (
                      <Fragment key={s.id}>
                        <tr className={open ? 'search-row open' : 'search-row'}>
                          <td>
                            <button
                              type="button"
                              className="expand-toggle"
                              onClick={() => toggle(s)}
                              aria-expanded={Boolean(open)}
                              aria-label={open ? 'Hide rides shown' : 'Show rides shown'}
                              disabled={s.resultCount === 0}
                              title={s.resultCount === 0 ? 'No rides matched' : undefined}
                            >
                              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          </td>
                          <td>{formatDateTime(s.createdAt)}</td>
                          <td>
                            <Link to={personPath(s.user.id)}>{s.user.fullName || `#${s.user.id}`}</Link>
                            {s.user.email && <div className="member-email">{s.user.email}</div>}
                          </td>
                          <td>{s.origin || '—'}</td>
                          <td>{s.destination || '—'}</td>
                          <td>{searchedDates(s)}</td>
                          <td>{slotsText(s.timeSlots)}</td>
                          <td className="numeric">
                            {s.resultCount > 0 ? (
                              <button type="button" className="count-link" onClick={() => toggle(s)}>
                                {s.resultCount}
                              </button>
                            ) : (
                              0
                            )}
                          </td>
                          <td className="numeric">{scoreText(s.topScore)}</td>
                        </tr>
                        {open && (
                          <tr className="search-rides-row">
                            <td colSpan={9}>
                              {open.loading && <div className="loading">Loading rides...</div>}
                              {open.error && <div className="alert alert-error">{open.error}</div>}
                              {!open.loading && !open.error && (
                                <>
                                  <p className="rides-note">
                                    The rides this search returned, in the order shown to the student
                                    {s.resultCount > open.rides.length && ` (top ${open.rides.length} of ${s.resultCount})`}.
                                    Ride details are as they are now.
                                  </p>
                                  <table className="rides-table">
                                    <thead>
                                      <tr>
                                        <th>Rank</th>
                                        <th>Score</th>
                                        <th>Ride</th>
                                        <th>Departure</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th title="Distance from the student's origin / destination to the ride's route">
                                          Pickup / drop-off
                                        </th>
                                        <th>Driver</th>
                                        <th>Price</th>
                                        <th>Seats</th>
                                        <th>Status</th>
                                        <th />
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {open.rides.map((r) => (
                                        <tr key={r.rideId}>
                                          <td className="numeric">{r.rank}</td>
                                          <td className="numeric">{scoreText(r.score)}</td>
                                          <td className="numeric">#{r.rideId}</td>
                                          <td>{departureText(r.ride)}</td>
                                          <td>{r.ride?.origin || '—'}</td>
                                          <td>{r.ride?.destination || '—'}</td>
                                          <td className="numeric">
                                            {kmText(r.pickupKm)} / {kmText(r.dropoffKm)}
                                          </td>
                                          <td>
                                            {r.ride?.driver ? (
                                              <Link to={personPath(r.ride.driver.id)}>
                                                {r.ride.driver.fullName || `#${r.ride.driver.id}`}
                                              </Link>
                                            ) : (
                                              '—'
                                            )}
                                          </td>
                                          <td className="numeric">{priceText(r.ride?.price ?? null)}</td>
                                          <td className="numeric">
                                            {r.ride ? `${r.ride.seatsBooked} / ${r.ride.seatsOffered}` : '—'}
                                          </td>
                                          <td>{r.ride?.status || 'not found'}</td>
                                          <td>
                                            <a href={r.link} target="_blank" rel="noopener noreferrer" className="open-ride-link">
                                              Open ride <ExternalLink size={13} />
                                            </a>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn btn-secondary btn-sm">
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {Math.max(1, pagination.pages)} ({pagination.total} total searches)
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchHistory;
